import { memo, useCallback, useEffect, useRef, useState } from 'react'
import * as THREE from 'three'
import { VideoType } from '../hooks/useCreateVideo'
import { config } from '../../config'

import { useFrame } from '@react-three/fiber'

type VideoViewerType = {
  src: string | null
  video: VideoType | null
  isLoop: boolean | null
  isMute: boolean | null
  //
  radius?: number | undefined | null
  position?: THREE.Vector3 | undefined | null
  marker?: number | undefined | null
  time?: number | undefined | null
  startMarker?: number | undefined | null
  // TODO:
  spaceId?: number | undefined | null
  //
  onLoaded: () => void | null
  onEnded?: () => void | undefined | null
  onMarkerPassed?: () => void | undefined | null
  onCheckPointPassed?: () => void | undefined | null
  onClick?: () => void | undefined | null
}

type CheckPoint = {
  time: number
  passed: boolean
}

export const VideoViewer = memo((props: VideoViewerType) => {
  const { spaceId, src, video, isLoop, isMute, marker, startMarker = 0, time = 0 } = props
  const {
    onLoaded = () => {}, onEnded = () => {}, onMarkerPassed = () => {}, onClick = () => {},
    onCheckPointPassed = () => {}
  } = props
  const { radius = 1000, position = new THREE.Vector3(0, 0, 0) } = props
  const [videoTexture, setVideoTexture] = useState<HTMLVideoElement | null>(null)
  const refMesh = useRef<THREE.Mesh>(null)
  const refGeometry = useRef<THREE.SphereGeometry>(null)
  const refMaterial = useRef<THREE.MeshBasicMaterial | null>(null)
  const refTexture = useRef<THREE.VideoTexture | null>(null)
  const isPlayPromiseWait = useRef(false)
  const isCleanUp = useRef(false)
  //
  const isMarkerPassed = useRef(false)
  const modifierRef = useRef<number>(1)
  const wrapRef = useRef<number>(0)
  const timeDataRef = useRef<Array<number>>([0, 0])
  const checkPointsIndexRef = useRef<number>(0)
  const checkPointsRef = useRef<Array<CheckPoint>>([
    { time: 11.78, passed: false },
    { time: 87.35, passed: false },
    { time: 102.64, passed: false },
    { time: 116.85, passed: false },
    { time: 145.58, passed: false },
    { time: 156.66, passed: false },
    { time: 177.88, passed: false },
    { time: 216.62, passed: false },
    { time: 253.09, passed: false },
    { time: 270.90, passed: false },
    { time: 305.17, passed: false },
    { time: 364.23, passed: false },
    { time: 393.26, passed: false },
    { time: 424.62, passed: false },
    { time: 507.57, passed: false },
    { time: 527.73, passed: false },
    //
    { time: 558.09, passed: false },
    { time: 566.67, passed: false },
    { time: 572.67, passed: false },
    { time: 578.01, passed: false },
    { time: 652.75, passed: false },
    //
    { time: 715.25, passed: false },
    { time: 761.43, passed: false },
    { time: 792.76, passed: false },
    { time: 805.50, passed: false },
    { time: 812.14, passed: false },
    { time: 866.10, passed: false },
    { time: 904.54, passed: false },
    { time: 922.52, passed: false },
    { time: 940.51, passed: false },
    { time: 949.08, passed: false },
    { time: 967.10, passed: false },
    { time: 973.51, passed: false },
    { time: 978.51, passed: false },
    { time: 983.48, passed: false },
    { time: 988.19, passed: false },
    { time: 1018.15, passed: false },
    { time: 1026.36, passed: false },
    { time: 1087.45, passed: false },
    { time: 1102.00, passed: false },
    { time: 1110.48, passed: false },
    { time: 1185.38, passed: false },
    { time: 1263.03, passed: false },
    { time: 1274.21, passed: false },
    { time: 1286.62, passed: false },
    { time: 1301.23, passed: false },
    { time: 1312.04, passed: false },
    { time: 1328.86, passed: false },
    { time: 1344.54, passed: false },
    { time: 1361.69, passed: false },
  ])
  //

  useEffect(() => {
    if (!video) {
      return
    }

    video.on('canplay', onCanplay)
    video.on('ended', onEnded)
    video.on('timeupdate', onTimeUpdate)

    return () => {
      video.pause() // TODO:
      video.off('canplay', onCanplay)
      video.off('ended', onEnded)
      video.off('timeupdate', onTimeUpdate)
    }
  }, [video])

  useEffect(() => {
    if (!video) {
      return
    }
    isPlayPromiseWait.current = true

    video.loop(isLoop)
    video.muted(isMute)
    video.autoplay(true)

    const replaceSrc =
      config.CLOUD_FRONT_S3_DOMAIN === 'media.whereness.io'
        ? src
        : src.replace(/https:\/\/(dev\.media|media)\.whereness\.io/, config.CLOUD_FRONT_S3_DOMAIN)
    video.src(replaceSrc)

    setVideoTexture(video.element)

    return () => {
      if (isPlayPromiseWait.current) {
        isCleanUp.current = true
      } else {
        onVideoCleanUp()
      }
    }
  }, [src, video])

  useEffect(() => {
    return () => {
      onVideoTextureCleanUp()
    }
  }, [videoTexture])

  const onVideoCleanUp = useCallback(() => {
    if (!video) {
      return
    }
    video.pause()
  }, [video])

  const onVideoTextureCleanUp = useCallback(() => {
    if (refGeometry.current) {
      refGeometry.current.dispose()
    }
    if (refMaterial.current) {
      refMaterial.current.dispose()
    }
    if (refMesh.current) {
      refMesh.current.remove()
    }
    if (refTexture.current) {
      refTexture.current.dispose()
    }
  }, [videoTexture])

  const onCanplay = useCallback(() => {
    if (!video || video.object === undefined) {
      return
    }

    // const dur = video.duration() as number
    // if (startMarker != 0) {
    //   if (startMarker < 1) {
    //     video.currentTime(Math.floor(Math.min(dur * startMarker, dur - 1)))
    //   } else {
    //     video.currentTime(Math.floor(Math.min(startMarker, dur - 1)))
    //   }
    // } else {
    //   video.currentTime(0)
    // }

    const playPromise = video.object.play()
    playPromise
      .then(() => {
        isPlayPromiseWait.current = false
        if (isCleanUp.current) {
          isCleanUp.current = false
          onVideoCleanUp()
        }
        wrapRef.current = 0
        modifierRef.current = 0
        isMarkerPassed.current = false
        //
        checkPointsIndexRef.current = 0;
        for (let i = 0; i < checkPointsRef.current.length; i ++) {
          checkPointsRef.current[i].passed = false
        }
        //

        const dur = video.duration() as number
        if (startMarker != 0) {
          if (startMarker < 1) {
            video.currentTime(Math.floor(Math.min(dur * startMarker, dur - 1)))
          } else {
            video.currentTime(Math.floor(Math.min(startMarker, dur - 1)))
          }
        } else {
          video.currentTime(0)
        }
      })
      .catch((error: any) => {
        isPlayPromiseWait.current = false
        if (isCleanUp.current) {
          isCleanUp.current = false
          onVideoCleanUp()
        }

        // TODO: detective
        console.error('play promise error', error)

        if (marker != null) {
          onMarkerPassed()
          setTimeout(() => {
            video.pause()
            onEnded()
          }, 200)
        }
        // --
      })

    onLoaded()
    video.off('canplay', onCanplay)
  }, [
    video, onVideoCleanUp,
    onMarkerPassed, onEnded, marker, startMarker
  ])

  /*
   * TODO:
   */
  const onTimeUpdate = useCallback(() => {
    if (!video) return

    const ct = video.currentTime() as number
    const dt = video.duration() as number
    if (ct == 0 && dt == 0) return;

    timeDataRef.current = [ct, dt]

    if (marker == null) return

    let cm = marker

    if (marker >= 1) {
      if (startMarker != 0 && startMarker >= 1) {
        cm = Math.min(startMarker + marker, dt - 0.5)
      } else if (startMarker != 0 && startMarker < 1) {
        cm = Math.min(dt * startMarker + marker, dt - 0.5)
      }
    } else {
      cm = Math.min(marker * dt, dt - 0.5)
    }

    if (ct >= cm && !isMarkerPassed.current) {
      // console.log(ct, dt, cm);
      isMarkerPassed.current = true;
      onMarkerPassed()
      setTimeout(() => {
        video.pause()
        onEnded()
      }, 150)
    }
  }, [video, onEnded, marker, startMarker, onMarkerPassed])

  useEffect(() => {
    if (!video) {
      return
    }
    video.muted(isMute)
  }, [isMute, video])

  useEffect(() => {
    if (!video) {
      return
    }
    video.currentTime(time * video.duration())
  }, [time])

  useFrame(_ => {
    /*
     * Transitions TODO:
     */

    if (
      checkPointsIndexRef.current < checkPointsRef.current.length
        && !checkPointsRef.current[checkPointsIndexRef.current].passed
        && checkPointsRef.current[checkPointsIndexRef.current].time - 0.5 <= timeDataRef.current[0]
    ) {
      // console.log('passed', checkPointsRef.current[checkPointsIndexRef.current])
      onCheckPointPassed()
      checkPointsRef.current[checkPointsIndexRef.current].passed = true
      checkPointsIndexRef.current ++
    }

    if (isMarkerPassed.current) {
      // if (video) {
      //   const vol = video.volume() as number
      //   video.volume(vol * 0.9)
      // }

      modifierRef.current *= 0.975

      const mat = refMesh.current?.material as THREE.Material
      try { mat.opacity = modifierRef.current } catch (err) {}
    } else {
      if (modifierRef.current < 1) {
        // if (modifierRef.current == 0) modifierRef.current = 0.01
        // modifierRef.current =  Math.min(modifierRef.current * 1.125, 1)
        modifierRef.current = 1
      }
    }
  })

  return (
    <>
      {videoTexture && (
        <mesh ref={refMesh} position={position} rotation-y={-Math.PI / 2} scale={[-1, 1, 1]} onClick={onClick}>
          <sphereGeometry ref={refGeometry} args={[radius, 130, 65]} />
          <meshBasicMaterial ref={refMaterial} side={THREE.BackSide} toneMapped={false} depthWrite={false} transparent={true}>
            <videoTexture
              ref={refTexture}
              attach="map"
              colorSpace={THREE.SRGBColorSpace}
              args={[videoTexture]}
              minFilter={THREE.LinearFilter}
              magFilter={THREE.LinearFilter}
            />
          </meshBasicMaterial>
        </mesh>
      )}
    </>
  )
})
